"use client";

import { Minus, Plus, Tag, Folder } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import React from "react";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();
  const [quantity, setQuantity] = React.useState(1);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square rounded-lg bg-muted/40 animate-pulse" />
        <div className="space-y-6">
          <div className="h-10 w-3/4 rounded-md bg-muted/40 animate-pulse" />
          <div className="h-6 w-1/3 rounded-md bg-muted/40 animate-pulse" />
          <div className="h-24 w-full rounded-md bg-muted/40 animate-pulse" />
          <div className="h-12 w-full rounded-md bg-muted/40 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Unable to retrieve fragrance details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Fragrance not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasSale =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price > 0 &&
    product.discounted_price !== product.price;

  const displayPrice = hasSale ? product.discounted_price : product.price;

  const increment = () => setQuantity((q) => q + 1);
  const decrement = () => setQuantity((q) => Math.max(1, q - 1));

  return (
    <div className="space-y-16">
      {}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {}
        <div className="relative">
          <div className="aspect-square w-full overflow-hidden rounded-lg bg-muted shadow-lg border border-border">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                Image unavailable
              </div>
            )}
          </div>
          {hasSale && (
            <Badge
              variant="default"
              className="absolute top-4 left-4 text-sm px-3 py-1 shadow-md bg-primary text-primary-foreground"
            >
              Exclusive Offer
            </Badge>
          )}
        </div>

        {}
        <div className="flex flex-col">
          {product.categories && product.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {product.categories.map((cat, i) => (
                <Link key={cat.id} to={cat.link} data-index={i}>
                  <Badge
                    variant="secondary"
                    className="text-xs uppercase tracking-wider bg-secondary text-secondary-foreground"
                  >
                    {cat.name}
                  </Badge>
                </Link>
              ))}
            </div>
          )}

          <h1 className="font-default text-4xl md:text-5xl font-semibold text-foreground leading-tight mb-6">
            {product.title}
          </h1>

          {}
          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-default text-3xl md:text-4xl font-bold text-foreground">
              ${Number(displayPrice).toFixed(2)}
            </span>
            {hasSale && (
              <span className="text-xl text-muted-foreground line-through">
                ${Number(product.price).toFixed(2)}
              </span>
            )}
          </div>

          {}
          {product.short_description && (
            <div
              className="text-base text-muted-foreground leading-relaxed mb-8 [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <Separator className="mb-8 bg-border" />

          {}
          <div className="flex flex-col sm:flex-row items-stretch gap-4 mb-8">
            <div className="flex items-center border border-border rounded-lg bg-card">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={decrement}
                aria-label="Decrease quantity"
                className="h-12 w-12 rounded-r-none"
              >
                <Minus className="size-4" />
              </Button>
              <div className="h-12 w-14 flex items-center justify-center font-default font-semibold text-foreground border-x border-border">
                {quantity}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={increment}
                aria-label="Increase quantity"
                className="h-12 w-12 rounded-l-none"
              >
                <Plus className="size-4" />
              </Button>
            </div>

            <AddToCartButton
              productId={product.id}
              quantity={quantity}
              variant="default"
              size="lg"
              className="w-full sm:flex-1"
              buttonClassName="bg-primary text-primary-foreground h-12 text-base font-semibold w-full rounded-lg"
              text="Add to Collection"
              showQuantity={false}
            />
          </div>

          {}
          <div className="space-y-3 pt-2">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-3 text-sm">
                <Folder className="size-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-default font-medium text-foreground">
                    Collection:
                  </span>
                  {product.categories.map((cat, i) => (
                    <Link
                      key={cat.id}
                      to={cat.link}
                      data-index={i}
                      className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                    >
                      {cat.name}
                      {i < product.categories.length - 1 && ","}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-3 text-sm">
                <Tag className="size-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-default font-medium text-foreground">
                    Notes:
                  </span>
                  {product.tags.map((tag, i) => (
                    <Link
                      key={tag.id}
                      to={tag.link}
                      data-index={i}
                      className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                    >
                      {tag.name}
                      {i < product.tags.length - 1 && ","}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      {product.long_description && (
        <div>
          <Separator className="mb-12 bg-border" />
          <div className="max-w-4xl mx-auto">
            <h2 className="font-default text-3xl md:text-4xl font-semibold text-foreground mb-6">
              The Essence
            </h2>
            <div
              className="prose prose-lg max-w-none text-foreground/90 leading-relaxed [&_p]:mb-4 [&_h2]:font-default [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:font-default [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_a]:text-primary [&_a]:underline-offset-4 [&_a:hover]:underline"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="806"
      id="single-product-template-section"
      className="bg-background text-foreground py-12 md:py-20"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}